import { ArrowLeftOutlined, PaperClipOutlined } from "@ant-design/icons";
import { Button, Collapse, Progress, Tag, Typography, notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import RequestAppAction from "src/store/slices/appActions";
import { useBulkSendProgress } from "src/hooks/useBulkSendProgress";
import { bulkSendBadge, formatBatchRef } from "src/utils/format";

const { Text } = Typography;

interface Attempt {
  id: number;
  status: string;
  sentCount: number;
  failedCount: number;
  createdAt: string;
  resentFromId: number | null;
}

interface ViewedStatus {
  totalCount: number;
  sentCount: number;
  failedCount: number;
  status: string;
  failedNumbers: string[];
  failedDetails?: { phoneNumber: string; reason: string | null }[];
}

interface BulkSendReopenProps {
  batch: { id: number; content: string; sentCount: number; failedCount: number; status: string; fileLocations?: string[] };
  onClose: () => void;
  sendDisabled?: boolean;
}


const BulkSendReopen = ({ batch, onClose, sendDisabled }: BulkSendReopenProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [viewedId, setViewedId] = useState<number>(batch.id);
  const [chain, setChain] = useState<Attempt[]>([]);
  const [chainLoaded, setChainLoaded] = useState(false);
  const [viewed, setViewed] = useState<ViewedStatus | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  // Store the new batchId returned by the resend call so onComplete can switch to it
  const newBatchIdRef = useRef<number | null>(null);

  // Wrap getBulkSendStatus in a Promise for useBulkSendProgress
  const pollStatus = useCallback(
    (batchId: number) =>
      new Promise<any>((resolve) => {
        dispatch(
          RequestAppAction.getBulkSendStatus({
            id: batchId,
            cbSuccess: (res: any) => resolve(res?.data ?? null),
            cbFailure: () => resolve(null),
          })
        );
      }),
    [dispatch]
  );

  const onComplete = useCallback(
    (final: any) => {
      // Refetch the chain to include the newly-created attempt
      dispatch(
        RequestAppAction.getBulkSendChain({
          id: batch.id,
          cbSuccess: (res: any) => setChain(res?.data ?? []),
          cbFailure: () => {},
        })
      );
      // Switch view to the new attempt that was just created by the resend
      if (newBatchIdRef.current !== null) {
        setViewedId(newBatchIdRef.current);
        newBatchIdRef.current = null;
      }
      setViewed(final);
      // Let the user know the resend finished and how it landed
      const sent = final?.sentCount ?? 0;
      const failed = final?.failedCount ?? 0;
      if (failed === 0) {
        notification.success({ message: t("notification.resendCompleteAllSent", { sent }) });
      } else {
        notification.warning({ message: t("notification.resendComplete", { sent, failed }) });
      }
    },
    [dispatch, batch.id, t]
  );

  const { phase, progress, start: startProgress } = useBulkSendProgress({
    pollStatus,
    onComplete,
  });

  // Fetch the resend chain once on mount
  useEffect(() => {
    dispatch(
      RequestAppAction.getBulkSendChain({
        id: batch.id,
        cbSuccess: (res: any) => {
          setChain(res?.data ?? []);
          setChainLoaded(true);
        },
        cbFailure: () => {
          setChain([]);
          setChainLoaded(true);
        },
      })
    );
    // Run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the status of the viewed attempt whenever viewedId changes
  useEffect(() => {
    dispatch(
      RequestAppAction.getBulkSendStatus({
        id: viewedId,
        cbSuccess: (res: any) => setViewed(res?.data ?? null),
        cbFailure: () => setViewed(null),
      })
    );
    // Re-run whenever the viewed attempt changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedId]);

  // Live-poll a still-processing batch so the history view tracks it in real time
  // Stops at a terminal status, and stays off while a resend drives its own progress
  useEffect(() => {
    if (!viewed) return;
    const terminal = viewed.status === "completed" || viewed.status === "failed";
    if (terminal || phase === "sending") return;
    const timer = setInterval(() => {
      dispatch(
        RequestAppAction.getBulkSendStatus({
          id: viewedId,
          cbSuccess: (res: any) => setViewed(res?.data ?? null),
          cbFailure: () => {},
        })
      );
    }, 3000);
    return () => clearInterval(timer);
    // Re-evaluate when the viewed batch, its status, or a resend starts or ends
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedId, viewed?.status, phase]);

  // The latest attempt is the one no other attempt points to as resentFromId
  const latestId: number | null =
    chain.length > 0
      ? chain.find((a) => !chain.some((b) => b.resentFromId === a.id))?.id ?? null
      : null;

  const isLatest = chain.length === 0 || latestId === viewedId;

  const failedNumbers: string[] = viewed?.failedNumbers ?? [];
  const failedCount: number = viewed?.failedCount ?? batch.failedCount;

  // A batch that has not reached a terminal status is still sending in the background
  const isProcessing: boolean =
    !!viewed && viewed.status !== "completed" && viewed.status !== "failed";

  const handleResendFailed = () => {
    setResendLoading(true);
    dispatch(
      RequestAppAction.postBulkResendAsync({
        id: viewedId,
        cbSuccess: (res: any) => {
          const body = res?.data ?? {};
          if (body.batchId && body.queuedCount) {
            newBatchIdRef.current = body.batchId;
            startProgress(body.batchId, body.queuedCount);
          } else {
            notification.info({ message: t("notification.bulkResultNoFailures") });
          }
          setResendLoading(false);
        },
        cbFailure: (e: string) => {
          setResendLoading(false);
          notification.error({ message: e || t("notification.bulkResultNoFailures") });
        },
      })
    );
  };

  // The outer container owns its own scroll so the parent does not need overflow handling
  return (
    <div
      style={{
        maxHeight: "70vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Scrollable content region */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 0 0.5rem" }}>
        {/* Header: reference id + status tag + resent tag + timestamp */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "1rem", flexWrap: "wrap" }}>
          <Text strong>{formatBatchRef(viewedId)}</Text>
          {viewed && (() => {
            const badge = bulkSendBadge({ status: viewed.status, sentCount: viewed.sentCount, failedCount: viewed.failedCount, isResent: !isLatest });
            return <Tag color={badge.color}>{t(badge.labelKey)}</Tag>;
          })()}
          {(() => {
            // Show the createdAt of the viewed attempt when available, else skip
            const viewedAttempt = chain.find((a) => a.id === viewedId);
            if (!viewedAttempt?.createdAt) return null;
            return (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {new Date(viewedAttempt.createdAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}{" "}
                {new Date(viewedAttempt.createdAt).toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </Text>
            );
          })()}
        </div>

        {/* Message accordion */}
        <Collapse
          style={{ marginBottom: "1rem" }}
          items={[
            {
              key: "message",
              label: t("fields.message"),
              children: (
                <div>
                  <Text style={{ whiteSpace: "pre-wrap" }}>{batch.content}</Text>
                  {batch.fileLocations && batch.fileLocations.length > 0 && (
                    <div style={{ marginTop: "0.5rem", display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {batch.fileLocations.map((url, index) => {
                        // Derive a readable name from the stored url and drop any query string
                        const name =
                          decodeURIComponent((url.split("/").pop() || "").split("?")[0]) ||
                          `Attachment ${index + 1}`;
                        return (
                          <a key={index} href={url} target="_blank" rel="noreferrer">
                            <Tag icon={<PaperClipOutlined />} color="blue" style={{ cursor: "pointer", marginInlineEnd: 0 }}>
                              {name}
                            </Tag>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />

        {/* Resend-chain timeline — only when more than one attempt exists */}
        {chain.length > 1 && (
          <div style={{ marginBottom: "1rem" }}>
            {chain.map((attempt) => (
              <div
                key={attempt.id}
                role="button"
                tabIndex={0}
                onClick={() => setViewedId(attempt.id)}
                onKeyDown={(e) => {
                  // Activate on Enter or Space to match button semantics
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setViewedId(attempt.id);
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  borderRadius: 4,
                  cursor: "pointer",
                  background: attempt.id === viewedId ? "#f0f5ff" : "transparent",
                  fontWeight: attempt.id === viewedId ? 600 : 400,
                  marginBottom: 4,
                }}
              >
                <Text>{formatBatchRef(attempt.id)}</Text>
                <Text type="secondary">
                  {attempt.sentCount} &#10003;&nbsp;&nbsp;{attempt.failedCount} &#10007;
                </Text>
                {(() => {
                  const badge = bulkSendBadge({ status: attempt.status, sentCount: attempt.sentCount, failedCount: attempt.failedCount, isResent: attempt.id !== latestId });
                  return <Tag color={badge.color}>{t(badge.labelKey)}</Tag>;
                })()}
              </div>
            ))}
          </div>
        )}

        {/* Progress bar while a resend is in flight */}
        {phase === "sending" ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "1rem 0",
            }}
          >
            <Progress
              style={{ width: "100%", marginBottom: "0.5rem" }}
              percent={
                progress.totalCount
                  ? Math.round(
                      ((progress.sentCount + progress.failedCount) / progress.totalCount) * 100
                    )
                  : 0
              }
            />
            <p style={{ margin: 0 }}>
              {t("notification.bulkSendProgress", {
                sentCount: progress.sentCount,
                totalCount: progress.totalCount,
              })}
            </p>
            {progress.failedCount > 0 && (
              <p style={{ margin: "0.25rem 0 0" }}>
                {t("notification.bulkResultFailedSend", { count: progress.failedCount })}
              </p>
            )}
          </div>
        ) : isProcessing ? (
          /* Live progress for a batch that is still sending in the background */
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "1rem 0",
            }}
          >
            <Progress
              style={{ width: "100%", marginBottom: "0.5rem" }}
              percent={
                viewed && viewed.totalCount
                  ? Math.round(
                      ((viewed.sentCount + viewed.failedCount) / viewed.totalCount) * 100
                    )
                  : 0
              }
            />
            <p style={{ margin: 0 }}>
              {t("notification.bulkSendProgress", {
                sentCount: viewed?.sentCount ?? 0,
                totalCount: viewed?.totalCount ?? 0,
              })}
            </p>
            {(viewed?.failedCount ?? 0) > 0 && (
              <p style={{ margin: "0.25rem 0 0" }}>
                {t("notification.bulkResultFailedSend", { count: viewed?.failedCount ?? 0 })}
              </p>
            )}
          </div>
        ) : (
          /* Failed recipients with their reason */
          failedNumbers.length > 0 && (
            <div>
              <Text strong style={{ display: "block", marginBottom: "0.5rem" }}>
                {t("notification.bulkFailedRecipients", { count: failedNumbers.length })}
              </Text>
              <div style={{ maxHeight: 180, overflowY: "auto" }}>
                {(viewed?.failedDetails && viewed.failedDetails.length
                  ? viewed.failedDetails
                  : failedNumbers.map((n) => ({ phoneNumber: n, reason: null }))
                ).map((item, index) => (
                  <div key={`failed-${index}`} style={{ padding: "2px 0" }}>
                    <Text>{item.phoneNumber}</Text>
                    {item.reason && (
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        {item.reason}
                      </Text>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>

      {/* Sticky footer — does not scroll with content */}
      <div
        style={{
          flexShrink: 0,
          borderTop: "1px solid #f0f0f0",
          padding: "0.75rem 0 0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Button
          icon={<ArrowLeftOutlined />}
          style={{ borderRadius: 50 }}
          onClick={onClose}
        >
          {t("button.backToList")}
        </Button>
        {phase !== "sending" && chainLoaded && isLatest && !isProcessing && !sendDisabled && failedCount > 0 && (
          <Button
            type="primary"
            style={{ borderRadius: 50 }}
            loading={resendLoading}
            disabled={resendLoading}
            onClick={handleResendFailed}
          >
            {t("heading.resendFailed")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BulkSendReopen;
